import { useState, useCallback, useMemo } from 'react';
import { DEFAULT_PAGE_SIZE, SORT_DIR } from '../utils/constants';
import { matchesSearch, searchTokens } from '../../../utils/search';

/**
 * Core table state management hook.
 * Provides internal state for sort, page, selection, and column filters
 * when the parent doesn't control them (uncontrolled mode).
 *
 * In controlled mode (when the parent passes onSortChange, onPageChange, etc.),
 * those callbacks are used instead of internal state.
 */
export default function useTableState({
    columns,
    data,
    totalRows,
    // Controlled pagination
    page: controlledPage,
    pageSize: controlledPageSize,
    onPageChange,
    onPageSizeChange,
    // Controlled sort
    sortKey: controlledSortKey,
    sortDir: controlledSortDir,
    onSortChange,
    serverSort = false,
    // Controlled search
    search: controlledSearch,
    onSearchChange,
    // Selection
    selectedRows: controlledSelected,
    onSelectionChange,
}) {
    // --- Internal state (used when uncontrolled) ---
    const [internalPage, setInternalPage] = useState(1);
    const [internalPageSize, setInternalPageSize] = useState(controlledPageSize || DEFAULT_PAGE_SIZE);
    const [internalSortKey, setInternalSortKey] = useState(controlledSortKey || null);
    const [internalSortDir, setInternalSortDir] = useState(controlledSortDir || SORT_DIR.ASC);
    const [internalSearch, setInternalSearch] = useState('');
    const [internalSelected, setInternalSelected] = useState([]);

    // --- Resolved values ---
    const page = controlledPage ?? internalPage;
    const pageSize = controlledPageSize ?? internalPageSize;
    const sortKey = controlledSortKey !== undefined ? controlledSortKey : internalSortKey;
    const sortDir = controlledSortDir !== undefined ? controlledSortDir : internalSortDir;
    const search = controlledSearch !== undefined ? controlledSearch : internalSearch;
    const selected = controlledSelected ?? internalSelected;

    // --- Handlers ---
    const handlePageChange = useCallback(
        (newPage) => {
            if (onPageChange) {
                onPageChange(newPage);
            } else {
                setInternalPage(newPage);
            }
        },
        [onPageChange]
    );

    const handlePageSizeChange = useCallback(
        (newSize) => {
            if (onPageSizeChange) {
                onPageSizeChange(newSize);
            } else {
                setInternalPageSize(newSize);
                setInternalPage(1);
            }
            // Reset page to 1 in controlled mode too
            if (onPageChange) onPageChange(1);
        },
        [onPageSizeChange, onPageChange]
    );

    const handleSortChange = useCallback(
        (key) => {
            const newDir =
                sortKey === key && sortDir === SORT_DIR.ASC ? SORT_DIR.DESC : SORT_DIR.ASC;
            if (onSortChange) {
                onSortChange(key, newDir);
            } else {
                setInternalSortKey(key);
                setInternalSortDir(newDir);
            }
        },
        [sortKey, sortDir, onSortChange]
    );

    const handleSearchChange = useCallback(
        (value) => {
            if (onSearchChange) {
                onSearchChange(value);
            } else {
                setInternalSearch(value);
                setInternalPage(1);
            }
        },
        [onSearchChange]
    );

    const handleSelectionChange = useCallback(
        (keys) => {
            if (onSelectionChange) {
                onSelectionChange(keys);
            } else {
                setInternalSelected(keys);
            }
        },
        [onSelectionChange]
    );

    const toggleRowSelection = useCallback(
        (rowKey) => {
            const next = selected.includes(rowKey)
                ? selected.filter((k) => k !== rowKey)
                : [...selected, rowKey];
            handleSelectionChange(next);
        },
        [selected, handleSelectionChange]
    );

    const toggleAllSelection = useCallback(
        (allKeys) => {
            const next = selected.length === allKeys.length ? [] : [...allKeys];
            handleSelectionChange(next);
        },
        [selected, handleSelectionChange]
    );

    // --- Derived data ---

    /**
     * Клиентский поиск — общее правило сервиса (utils/search.js): слова запроса ищутся целиком или
     * частью в ЛЮБОМ поле строки, не только в видимых колонках (артикул, SKU, название и т.д.).
     * Колонки с `searchable: false` (кнопки, картинки) исключаются, `searchValue(row)` добавляет
     * текст, которого нет в данных. При serverSort фильтрует сервер — не вмешиваемся.
     */
    const filteredData = useMemo(() => {
        const tokens = searchTokens(search);
        if (serverSort || !tokens.length || !data.length) return data;
        const hidden = new Set(columns.filter((col) => col.searchable === false).map((col) => col.key));
        const extra = columns.filter((col) => typeof col.searchValue === 'function');
        return data.filter((row) => {
            const base = Array.isArray(row)
                ? row.filter((_, index) => !hidden.has(columns[index]?.key))
                : Object.fromEntries(Object.entries(row || {}).filter(([key]) => !hidden.has(key)));
            return matchesSearch([base, extra.map((col) => col.searchValue(row))], tokens);
        });
    }, [data, search, serverSort, columns]);

    const resolvedTotalRows = totalRows ?? filteredData.length;
    const effectivePageSize = pageSize === 'all' ? resolvedTotalRows : pageSize;
    const totalPages = Math.max(1, Math.ceil(resolvedTotalRows / effectivePageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);

    // Client-side sorting (when serverSort is false)
    const sortedData = useMemo(() => {
        if (serverSort || !sortKey || !filteredData.length) return filteredData;
        const col = columns.find((c) => c.key === sortKey);
        if (!col) return filteredData;

        const sorted = [...filteredData].sort((a, b) => {
            const aVal = Array.isArray(a) ? a[columns.indexOf(col)] : a[sortKey];
            const bVal = Array.isArray(b) ? b[columns.indexOf(col)] : b[sortKey];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            if (col.type === 'number') {
                return Number(aVal) - Number(bVal);
            }
            return String(aVal).localeCompare(String(bVal), 'ru');
        });

        return sortDir === SORT_DIR.DESC ? sorted.reverse() : sorted;
    }, [filteredData, sortKey, sortDir, serverSort, columns]);

    // Client-side pagination (when serverSort is false — data is all in memory)
    const paginatedData = useMemo(() => {
        if (serverSort) return data; // Server already paginated
        const start = (safePage - 1) * effectivePageSize;
        return sortedData.slice(start, start + effectivePageSize);
    }, [sortedData, safePage, effectivePageSize, serverSort, data]);

    const sliceStart = serverSort ? 0 : (safePage - 1) * effectivePageSize;

    return {
        // State
        page: safePage,
        pageSize,
        sortKey,
        sortDir,
        search,
        selected,
        // Derived
        totalPages,
        totalRows: resolvedTotalRows,
        displayData: paginatedData,
        // Все строки после поиска и сортировки, без пагинации — для выгрузки.
        allData: serverSort ? data : sortedData,
        sliceStart,
        effectivePageSize,
        // Handlers
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        handleSearchChange,
        handleSelectionChange,
        toggleRowSelection,
        toggleAllSelection,
    };
}
